/**
 * Campaign Form Component
 *
 * Multi-step wizard for creating and editing email campaigns
 * Steps: 1) Details, 2) Audience, 3) Content, 4) Preview
 */
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/hooks/use-toast';
import { ArrowLeft, ArrowRight, Check, Loader2, Mail, Users, FileText, Eye, CalendarIcon, Save, AlertCircle, AlertTriangle, Info, FolderOpen, Monitor, Smartphone, Tablet } from 'lucide-react';
import { useAuth } from '@/context/auth';
import EmailService, { EmailCampaign } from '@/api/email';
import AudienceSelector from './AudienceSelector';
import EmailEditor from './EmailEditor';
import GrapesJSEditor from '../editor/GrapesJSEditor';
import { DomainSelector } from './DomainSelector';
import { useVerifiedDomains } from '@/hooks/useVerifiedDomains';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  extractVariablesFromContent,
  validateRequiredVariables,
  getValidationMessages,
  groupVariablesByCategory,
  getVariableMetadata
} from '@/utils/email/variableHelpers';
import { CATEGORY_LABELS, type EmailVariableCategory } from '../editor/variables/types';
import PreviewDialog from '../preview/PreviewDialog';
import { TemplateLibraryModal, TemplateSaveDialog } from '../templates';
import { supabase } from '@/integrations/supabase/client';

interface CampaignFormProps {
  campaignId?: string; // For editing existing campaigns
  onSave?: (campaign: EmailCampaign) => void;
  onCancel?: () => void;
}

type FormStep = 'details' | 'audience' | 'content' | 'preview';

interface FormData {
  campaign_name: string;
  campaign_subject: string;
  description: string;
  campaign_content_html: string;
  campaign_content_text: string;
  campaign_design_json?: any; // GrapesJS project data
  template_id?: string | null; // Template UUID used to create campaign
  audience_type: 'all' | 'filtered' | 'custom';
  audience_filters: Record<string, any>;
  selected_restaurants: string[];
  schedule_type: 'now' | 'scheduled';
  schedule_date: Date | null;
  schedule_time: string;
  email_domain_id: string;
  email_prefix: string;
  sender_name: string;
}

const CampaignForm: React.FC<CampaignFormProps> = ({ campaignId, onSave, onCancel }) => {
  const { user } = useAuth();
  console.log('[CampaignForm] User data:', {
    organisationId: user?.organisationId,
    restaurantId: user?.restaurantId,
    fullUser: user
  });
  const [currentStep, setCurrentStep] = useState<FormStep>('details');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [estimatedRecipients, setEstimatedRecipients] = useState(0);
  const [estimatedCost, setEstimatedCost] = useState(0);

  // Track the actual campaign ID (either passed in or created as draft)
  const [activeCampaignId, setActiveCampaignId] = useState<string | undefined>(campaignId);

  // Store reference to GrapesJS manual save function and editor instance
  const editorSaveRef = React.useRef<(() => Promise<void>) | null>(null);
  const editorInstanceRef = React.useRef<any | null>(null);

  // State for manual save button
  const [manualSaving, setManualSaving] = useState(false);

  // State for template modals
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false);
  const [showTemplateSaveDialog, setShowTemplateSaveDialog] = useState(false);

  // Phase 4.2b: Customer preview with variable resolution
  const [recentCustomers, setRecentCustomers] = useState<Array<{
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    total_orders?: number;
  }>>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [resolvedPreview, setResolvedPreview] = useState<{
    html: string;
    subject: string;
    resolved_variables: Record<string, Record<string, string>>;
    customer: { id: string; email: string; first_name: string; last_name: string };
  } | null>(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);

  // Device preview size for Step 4 email preview
  type DeviceSize = 'mobile' | 'tablet' | 'desktop';
  const [previewDeviceSize, setPreviewDeviceSize] = useState<DeviceSize>('desktop');

  // Load verified domains for constructing sender email
  const { domains } = useVerifiedDomains(user?.organisationId || null);

  const [formData, setFormData] = useState<FormData>({
    campaign_name: '',
    campaign_subject: '',
    description: '',
    campaign_content_html: '',
    campaign_content_text: '',
    campaign_design_json: undefined,
    template_id: null,
    audience_type: 'all',
    audience_filters: {},
    selected_restaurants: ['all'],
    schedule_type: 'now',
    schedule_date: null,
    schedule_time: '12:00',
    email_domain_id: '',
    email_prefix: '',
    sender_name: ''
  });

  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  // Generate time options (15-minute intervals)
  const times = Array.from({ length: 24 * 4 }, (_, i) => {
    const hour = Math.floor(i / 4);
    const minute = (i % 4) * 15;
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  });

  // Load existing campaign if editing
  useEffect(() => {
    if (campaignId) {
      // Editing existing campaign
      loadCampaign();
    }
    // Note: We no longer auto-create draft campaigns on mount
    // Draft will be created when user moves from Step 1 to Step 2
  }, [campaignId]);

  const loadCampaign = async () => {
    if (!campaignId) return;

    try {
      setLoading(true);
      const campaign = await EmailService.getCampaign(campaignId);

      // Parse scheduling data
      let scheduleDate: Date | null = null;
      let scheduleTime: string = '12:00';
      let scheduleType: 'now' | 'scheduled' = 'now';

      // Convert DB schedule_type ('immediate' or 'scheduled') to form type ('now' or 'scheduled')
      if (campaign.schedule_type === 'scheduled' && campaign.schedule_time) {
        scheduleType = 'scheduled';

        // Parse UTC timestamp and convert to NZ local time
        const utcDate = new Date(campaign.schedule_time);

        // Convert to NZ timezone (Pacific/Auckland)
        const nzDate = new Date(utcDate.toLocaleString('en-US', { timeZone: 'Pacific/Auckland' }));

        scheduleDate = nzDate;

        // Extract time in HH:mm format
        const hours = nzDate.getHours().toString().padStart(2, '0');
        const minutes = nzDate.getMinutes().toString().padStart(2, '0');
        scheduleTime = `${hours}:${minutes}`;
      } else {
        // 'immediate' or no schedule_time
        scheduleType = 'now';
      }

      setFormData({
        campaign_name: campaign.campaign_name,
        campaign_subject: campaign.campaign_subject,
        description: campaign.description || '',
        campaign_content_html: campaign.campaign_content_html,
        campaign_content_text: campaign.campaign_content_text || '',
        campaign_design_json: campaign.design_json || undefined,
        template_id: campaign.template_id || null,
        audience_type: campaign.audience_type,
        audience_filters: campaign.audience_filters || {},
        selected_restaurants: campaign.selected_restaurants?.length > 0 ? campaign.selected_restaurants : ['all'],
        schedule_type: scheduleType,
        schedule_date: scheduleDate,
        schedule_time: scheduleTime,
        email_domain_id: campaign.email_domain_id || '',
        email_prefix: '', // Will be extracted from sender_email if available
        sender_name: campaign.sender_name || ''
      });
    } catch (error) {
      console.error('Error loading campaign:', error);
      toast({
        title: 'Error',
        description: 'Failed to load campaign details',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Update form field
  const updateField = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Validate current step
  const validateStep = (step: FormStep): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (step === 'details') {
      if (!formData.campaign_name.trim()) {
        newErrors.campaign_name = 'Campaign name is required';
      }
      if (!formData.campaign_subject.trim()) {
        newErrors.campaign_subject = 'Subject line is required';
      }
      if (!formData.email_domain_id) {
        newErrors.email_domain_id = 'Email domain is required';
      }
      if (!formData.email_prefix.trim()) {
        newErrors.email_prefix = 'Email prefix is required';
      }
      if (!formData.sender_name.trim()) {
        newErrors.sender_name = 'Sender name is required';
      }
    }

    if (step === 'content') {
      if (!formData.campaign_content_html.trim()) {
        newErrors.campaign_content_html = 'Email content is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Navigation
  /**
   * Handle clicking on a step indicator in the progress bar
   * Saves current step before navigating to prevent data loss
   * Special handling for content step (GrapesJS save)
   */
  const handleStepClick = async (targetStep: FormStep) => {
    // Don't navigate if clicking on current step
    if (targetStep === currentStep) {
      console.log('[Campaign Form] Already on step:', targetStep);
      return;
    }

    console.log(`[Campaign Form] Step navigation: ${currentStep} → ${targetStep}`);

    // Determine navigation direction
    const currentOrder = getStepOrder(currentStep);
    const targetOrder = getStepOrder(targetStep);
    const isMovingForward = targetOrder > currentOrder;

    // SPECIAL CASE: Content step (skip validation, content in GrapesJS)
    if (currentStep === 'content') {
      try {
        // ✅ Trigger GrapesJS save first to ensure proper plain text generation
        if (editorSaveRef.current) {
          console.log('[Campaign Form] Triggering GrapesJS save before step transition');
          await editorSaveRef.current();  // Calls editor.store() with comprehensive plain text stripping
          // Wait for state updates to complete
          await new Promise(resolve => setTimeout(resolve, 300));
          console.log('[Campaign Form] GrapesJS save completed, state updated');
        }

        await saveCurrentStep();  // Save campaign data
        console.log('[Campaign Form] Content step save completed before navigation');
      } catch (error) {
        console.error('[Campaign Form] Content step save failed:', error);
        // Don't prevent navigation - content might still be saved
        // Only show error for forward navigation
        if (isMovingForward) {
          toast({
            title: 'Save Warning',
            description: 'Content save encountered an error, but you can continue. Try saving manually if needed.',
            variant: 'default'
          });
        }
      }
    } else {
      // OTHER STEPS: Validate when moving forward, skip when moving backward
      if (isMovingForward) {
        // Validate before proceeding forward
        if (!validateStep(currentStep)) {
          toast({
            title: 'Validation Error',
            description: 'Please fix the errors before proceeding',
            variant: 'destructive'
          });
          return; // Block forward navigation
        }
      }
      // Always save current step data (even when moving backward)
      try {
        await saveCurrentStep();
        console.log(`[Campaign Form] ${currentStep} step saved before navigation`);
      } catch (error) {
        console.error(`[Campaign Form] ${currentStep} step save failed:`, error);
        // Only block forward navigation on save errors
        if (isMovingForward) {
          toast({
            title: 'Save Error',
            description: 'Failed to save current step. Please try again.',
            variant: 'destructive'
          });
          return;
        }
        // Allow backward navigation even with save errors (user might be fixing issues)
      }
    }

    // Navigate to target step
    setCurrentStep(targetStep);
    console.log(`[Campaign Form] Navigation complete: now on ${targetStep}`);
  };

  const getStepOrder = (step: FormStep): number => {
    const order = { details: 1, audience: 2, content: 3, preview: 4 };
    return order[step];
  };

  const nextStep = async () => {
    // For content step, skip validation (content is in GrapesJS, not in formData yet)
    // The save process will handle getting the content
    if (currentStep === 'content') {
      // Just save - validation happens in saveCurrentStep
      try {
        await saveCurrentStep();
        console.log('[Campaign Form] Content step save completed successfully');
      } catch (error) {
        console.error('[Campaign Form] Content step save failed:', error);
        // Don't prevent navigation even if save fails - content might still be there
      }
    } else {
      // For other steps, validate first then save
      if (!validateStep(currentStep)) return;
      await saveCurrentStep();
    }

    const steps: FormStep[] = ['details', 'audience', 'content', 'preview'];
    const currentIndex = steps.indexOf(currentStep);
    const nextStepName = steps[currentIndex + 1];

    console.log('[Campaign Form] Transitioning from', currentStep, 'to', nextStepName);

    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1]);
      console.log('[Campaign Form] Step transition complete');
    }
  };

  // Save data for the current step
  const saveCurrentStep = async () => {
    try {
      setSaving(true);

      // For content step, trigger GrapesJS save first to ensure content is up-to-date
      if (currentStep === 'content' && editorSaveRef.current) {
        console.log('[Campaign Form] Triggering GrapesJS save before step transition');
        await editorSaveRef.current();
        // Wait for the onChange callback to update formData
        // This needs to be long enough for React state updates to complete
        await new Promise(resolve => setTimeout(resolve, 300));
        console.log('[Campaign Form] Waited for state update to complete');
      }

      // If no campaign exists yet (new campaign), create draft when leaving Step 1
      if (!activeCampaignId && currentStep === 'details' && user?.organisationId) {
        console.log('[Campaign Form] Creating draft campaign...');

        let scheduleTime = undefined;
        if (formData.schedule_type === 'scheduled' && formData.schedule_date && formData.schedule_time) {
          const date = new Date(formData.schedule_date);
          const [hours, minutes] = formData.schedule_time.split(':');
          date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
          scheduleTime = date.toISOString();
        }

        const selectedDomain = domains.find(d => d.id === formData.email_domain_id);
        const senderEmail = selectedDomain && formData.email_prefix
          ? `${formData.email_prefix}@${selectedDomain.domain}`
          : undefined;

        const draftData = {
          campaign_name: formData.campaign_name,
          campaign_subject: formData.campaign_subject,
          description: formData.description,
          campaign_content_html: '<p>Start designing your email...</p>', // Minimal content for validation
          campaign_content_text: 'Start designing your email...',
          status: 'draft',
          organisation_id: user.organisationId,
          email_domain_id: formData.email_domain_id || undefined,
          sender_email: senderEmail,
          sender_name: formData.sender_name || undefined,
          schedule_type: formData.schedule_type === 'now' ? 'immediate' : 'scheduled',
          schedule_time: scheduleTime,
        };

        const draft = await EmailService.createCampaign(draftData);
        setActiveCampaignId(draft.id);
        console.log('[Campaign Form] Draft campaign created:', draft.id);
        return; // Exit early after creation
      }

      // For existing campaigns, update based on current step
      if (!activeCampaignId) {
        console.warn('[Campaign Form] No campaign ID available for autosave');
        return;
      }

      // Prepare data based on current step
      let updateData: any = {};

      if (currentStep === 'details') {
        // Step 1: Save campaign details and scheduling
        let scheduleTime = undefined;
        if (formData.schedule_type === 'scheduled' && formData.schedule_date && formData.schedule_time) {
          const date = new Date(formData.schedule_date);
          const [hours, minutes] = formData.schedule_time.split(':');
          date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
          scheduleTime = date.toISOString();
        }

        const selectedDomain = domains.find(d => d.id === formData.email_domain_id);
        const senderEmail = selectedDomain && formData.email_prefix
          ? `${formData.email_prefix}@${selectedDomain.domain}`
          : undefined;

        updateData = {
          campaign_name: formData.campaign_name,
          campaign_subject: formData.campaign_subject,
          description: formData.description,
          email_domain_id: formData.email_domain_id || undefined,
          sender_email: senderEmail,
          sender_name: formData.sender_name || undefined,
          schedule_type: formData.schedule_type === 'now' ? 'immediate' : 'scheduled',
          schedule_time: scheduleTime,
        };
      } else if (currentStep === 'audience') {
        // Step 2: Save audience configuration
        updateData = {
          audience_type: formData.audience_type,
          audience_filters: formData.audience_filters,
          selected_restaurants: formData.selected_restaurants.includes('all') ? [] : formData.selected_restaurants,
        };
      } else if (currentStep === 'content') {
        // Step 3: Content is already saved by GrapesJS storage manager
        // Save only template_id to preserve template tracking without overwriting content
        console.log('[Campaign Form] Content step - saving template_id (content saved by GrapesJS)');
        updateData = {
          template_id: formData.template_id || null
        };
      }

      // Only save if there's data to update
      if (Object.keys(updateData).length > 0) {
        await EmailService.updateCampaign(activeCampaignId, updateData);
        console.log(`[Campaign Form] Auto-saved ${currentStep} step data`);
      }
    } catch (error) {
      console.error(`[Campaign Form] Error auto-saving ${currentStep} step:`, error);
      // Don't show error toast for autosave failures - just log it
      // The user can continue and try again on final save
    } finally {
      setSaving(false);
    }
  };

  const previousStep = () => {
    const steps: FormStep[] = ['details', 'audience', 'content', 'preview'];
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1]);
    }
  };

  // Manual save for content step - uses same mechanism as Next button
  const handleManualSave = async () => {
    try {
      setManualSaving(true);
      console.log('[Campaign Form] Manual save triggered');

      // Use the exact same save mechanism as the Next button
      await saveCurrentStep();

      console.log('[Campaign Form] Manual save completed successfully');
      toast({
        title: 'Saved',
        description: 'Your email content has been saved successfully',
      });
    } catch (error) {
      console.error('[Campaign Form] Manual save error:', error);
      toast({
        title: 'Save Error',
        description: 'Failed to save email content',
        variant: 'destructive'
      });
    } finally {
      setManualSaving(false);
    }
  };

  // Template handlers
  const handleTemplateSelect = async (designJson: Record<string, any>, templateId?: string) => {
    console.log('[Campaign Form] Template selected:', templateId);

    if (editorInstanceRef.current) {
      try {
        // Load template into GrapesJS editor
        editorInstanceRef.current.loadProjectData(designJson);

        // IMPORTANT: After loading template, restore user's media library assets
        // This prevents the template's empty assets array from wiping out the asset manager
        if (user?.organisationId) {
          try {
            const { data: mediaAssets, error } = await supabase
              .from('restaurant_media')
              .select('id, public_url, original_filename, media_type')
              .eq('organisation_id', user.organisationId)
              .eq('media_type', 'image')
              .eq('status', 'completed')
              .order('created_at', { ascending: false });

            if (!error && mediaAssets && mediaAssets.length > 0) {
              const assetManager = editorInstanceRef.current.AssetManager;

              // Clear existing assets and add media library images
              assetManager.clear();

              // IMPORTANT: Reverse array before adding because AssetManager.add() prepends
              // Query returns newest-first, reverse makes oldest-first for prepending
              // Final result: newest at top
              mediaAssets.reverse().forEach((asset) => {
                assetManager.add({
                  type: 'image',
                  src: asset.public_url,
                  name: asset.original_filename || 'Image',
                  id: asset.id
                });
              });

              console.log(`[Campaign Form] Loaded ${mediaAssets.length} assets from media library`);
            }
          } catch (assetError) {
            console.error('[Campaign Form] Error loading media library assets:', assetError);
            // Non-fatal error - template still loads, just without pre-populated assets
          }
        }

        // Update form data
        setFormData(prev => ({
          ...prev,
          template_id: templateId || null,
          campaign_design_json: designJson
        }));

        // Close template library
        setShowTemplateLibrary(false);

        toast({
          title: 'Template Loaded',
          description: 'Template has been loaded into the editor. You can now customize it.',
        });
      } catch (error) {
        console.error('[Campaign Form] Error loading template:', error);
        toast({
          title: 'Error',
          description: 'Failed to load template into editor',
          variant: 'destructive'
        });
      }
    } else {
      console.error('[Campaign Form] Editor instance not available');
      toast({
        title: 'Error',
        description: 'Editor not ready. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const handleSaveAsTemplate = async () => {
    console.log('[Campaign Form] Save as template clicked');

    // First, trigger a save to ensure we have the latest content
    if (editorSaveRef.current) {
      try {
        await editorSaveRef.current();
        console.log('[Campaign Form] Editor saved before opening template dialog');
      } catch (error) {
        console.error('[Campaign Form] Error saving before template dialog:', error);
      }
    }

    setShowTemplateSaveDialog(true);
  };

  const handleTemplateSaved = (templateId: string) => {
    console.log('[Campaign Form] Template saved:', templateId);
    toast({
      title: 'Template Saved',
      description: 'Your design has been saved as a template for future use.',
    });
  };

  // Save campaign
  const handleSave = async (sendImmediately: boolean = false) => {
    if (!user?.organisationId) {
      toast({
        title: 'Error',
        description: 'No organisation context available',
        variant: 'destructive'
      });
      return;
    }

    // Final validation
    if (!validateStep('details') || !validateStep('content')) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        variant: 'destructive'
      });
      return;
    }

    try {
      setSaving(true);

      // ✅ Trigger GrapesJS save first to ensure proper plain text is in database
      // This ensures campaign_content_text has comprehensive HTML stripping
      if (editorSaveRef.current) {
        console.log('[Campaign Form] Triggering GrapesJS save before final save');
        try {
          await editorSaveRef.current();  // Saves with comprehensive plain text stripping
          await new Promise(resolve => setTimeout(resolve, 300));
          console.log('[Campaign Form] GrapesJS save completed before final save');
        } catch (error) {
          console.warn('[Campaign Form] GrapesJS save error (non-fatal):', error);
        }
      }

      // Prepare schedule_time - combine date and time if scheduled
      let scheduleTime = undefined;
      if (formData.schedule_type === 'scheduled' && formData.schedule_date && formData.schedule_time) {
        // Combine date and time into ISO timestamp
        const date = new Date(formData.schedule_date);
        const [hours, minutes] = formData.schedule_time.split(':');
        date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        scheduleTime = date.toISOString();
      }

      // Construct full sender_email from domain and prefix
      const selectedDomain = domains.find(d => d.id === formData.email_domain_id);
      const senderEmail = selectedDomain && formData.email_prefix
        ? `${formData.email_prefix}@${selectedDomain.domain}`
        : undefined;

      const campaignData = {
        organisation_id: user.organisationId,
        restaurant_id: user.restaurantId || null,
        selected_restaurants: formData.selected_restaurants.includes('all') ? [] : formData.selected_restaurants,
        campaign_name: formData.campaign_name,
        campaign_subject: formData.campaign_subject,
        // ✅ Don't include campaign_content_html and campaign_content_text here
        // They were already saved by GrapesJS manual save above with proper plain text stripping
        // Including them here would overwrite the good plain text with poor quality from onChange
        description: formData.description,
        template_id: formData.template_id || null,
        audience_type: formData.audience_type,
        audience_filters: formData.audience_filters,
        schedule_type: formData.schedule_type === 'now' ? 'immediate' : 'scheduled',
        schedule_time: scheduleTime,
        email_domain_id: formData.email_domain_id || undefined,
        sender_email: senderEmail,
        sender_name: formData.sender_name || undefined
      };

      let savedCampaign: EmailCampaign;

      if (activeCampaignId) {
        // Update existing campaign (or draft)
        savedCampaign = await EmailService.updateCampaign(activeCampaignId, campaignData);
        toast({
          title: 'Success',
          description: 'Campaign updated successfully'
        });
      } else {
        // Create new campaign
        savedCampaign = await EmailService.createCampaign(campaignData);
        toast({
          title: 'Success',
          description: 'Campaign created successfully'
        });
      }

      // Send immediately if requested
      if (sendImmediately && savedCampaign.id) {
        await EmailService.sendCampaign(savedCampaign.id, { confirm: true });
        toast({
          title: 'Campaign Sent',
          description: `Campaign "${savedCampaign.campaign_name}" has been queued for sending`
        });
      }

      if (onSave) {
        onSave(savedCampaign);
      }
    } catch (error: any) {
      console.error('Error saving campaign:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save campaign',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle audience change
  const handleAudienceChange = (audienceData: {
    audience_type: 'all' | 'filtered' | 'custom';
    audience_filters: Record<string, any>;
    estimatedRecipients: number;
    estimatedCost: number;
    selectedRestaurants: string[];
  }) => {
    setFormData(prev => ({
      ...prev,
      audience_type: audienceData.audience_type,
      audience_filters: audienceData.audience_filters,
      selected_restaurants: audienceData.selectedRestaurants
    }));
    setEstimatedRecipients(audienceData.estimatedRecipients);
    setEstimatedCost(audienceData.estimatedCost);
  };

  // Fetch audience preview when entering preview step (if not already loaded)
  useEffect(() => {
    const fetchAudiencePreview = async () => {
      if (currentStep === 'preview' && estimatedRecipients === 0 && user?.organisationId) {
        try {
          console.log('[Campaign Form] Fetching audience preview for preview step');

          // Convert selected_restaurants to array for backend
          // 'all' means no filter (empty array)
          const restaurantIdsToSend = formData.selected_restaurants.includes('all')
            ? []
            : formData.selected_restaurants;

          const response = await EmailService.previewAudience(user.organisationId, {
            restaurant_ids: restaurantIdsToSend,
            audience_type: formData.audience_type,
            audience_filters: formData.audience_type === 'filtered' ? formData.audience_filters : {}
          });

          setEstimatedRecipients(response.total_recipients);
          setEstimatedCost(parseFloat(response.estimated_cost));
          console.log('[Campaign Form] Audience preview loaded:', response.total_recipients, 'recipients');
        } catch (error) {
          console.error('[Campaign Form] Error fetching audience preview:', error);
          // Don't show error toast - just leave at 0
        }
      }
    };

    fetchAudiencePreview();
  }, [currentStep, estimatedRecipients, user?.organisationId, formData.selected_restaurants, formData.audience_type, formData.audience_filters]);

  // Phase 4.2b: Fetch recent customers when entering preview step
  useEffect(() => {
    const fetchRecentCustomers = async () => {
      if (currentStep === 'preview' && user?.organisationId && recentCustomers.length === 0) {
        try {
          console.log('[Campaign Form] Fetching recent customers for preview');
          const customers = await EmailService.getRecentCustomers(user.organisationId, 50);
          setRecentCustomers(customers);
          // Auto-select first customer
          if (customers.length > 0) {
            setSelectedCustomerId(customers[0].id);
          }
        } catch (error) {
          console.error('[Campaign Form] Error fetching customers:', error);
          toast({
            title: 'Warning',
            description: 'Could not load customers for preview',
            variant: 'default'
          });
        }
      }
    };

    fetchRecentCustomers();
  }, [currentStep, user?.organisationId, recentCustomers.length]);

  // Phase 4.2b: Generate preview with resolved variables
  const handleGeneratePreview = async () => {
    if (!selectedCustomerId || !activeCampaignId || !user?.organisationId) {
      toast({
        title: 'Error',
        description: 'Please select a customer first',
        variant: 'destructive'
      });
      return;
    }

    setPreviewLoading(true);
    try {
      console.log('[Campaign Form] Generating preview for customer:', selectedCustomerId);
      const preview = await EmailService.previewWithVariables(
        activeCampaignId,
        selectedCustomerId,
        user.organisationId
      );
      setResolvedPreview(preview);
      setShowPreviewDialog(true); // Open the dialog with the preview
      toast({
        title: 'Success',
        description: 'Preview generated with actual customer data',
        variant: 'default'
      });
    } catch (error: any) {
      console.error('[Campaign Form] Error generating preview:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to generate preview',
        variant: 'destructive'
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  // Handle content change
  const handleContentChange = (html: string, text: string) => {
    setFormData(prev => ({
      ...prev,
      campaign_content_html: html,
      campaign_content_text: text
    }));
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Loading campaign...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">
            {campaignId ? 'Edit Campaign' : 'Create Campaign'}
          </h2>
          <p className="text-muted-foreground">
            {campaignId
              ? 'Modify your email campaign settings'
              : 'Create a new email campaign to engage your customers'
            }
          </p>
        </div>
        {onCancel && (
          <Button variant="outline" onClick={onCancel}>
            Back to Campaigns
          </Button>
        )}
      </div>

      {/* Step Indicator */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            {[
              { id: 'details', label: 'Details', icon: FileText },
              { id: 'audience', label: 'Audience', icon: Users },
              { id: 'content', label: 'Content', icon: Mail },
              { id: 'preview', label: 'Preview', icon: Eye }
            ].map((step, index, array) => (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center gap-2">
                  <Button
                    variant={currentStep === step.id ? 'default' : 'outline'}
                    size="icon"
                    className="rounded-full"
                    onClick={() => handleStepClick(step.id as FormStep)}
                  >
                    <step.icon className="h-4 w-4" />
                  </Button>
                  <span className="text-xs font-medium">{step.label}</span>
                </div>
                {index < array.length - 1 && (
                  <div className="flex-1 h-[2px] bg-border mx-2" />
                )}
              </React.Fragment>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Step 1: Campaign Details */}
      {currentStep === 'details' && (
        <Card>
          <CardHeader>
            <CardTitle>Campaign Details</CardTitle>
            <CardDescription>
              Set the name, subject line, and basic information for your campaign
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="campaign_name">Campaign Name *</Label>
              <Input
                id="campaign_name"
                placeholder="e.g., Summer Sale 2025"
                value={formData.campaign_name}
                onChange={(e) => updateField('campaign_name', e.target.value)}
              />
              {errors.campaign_name && (
                <p className="text-sm text-destructive">{errors.campaign_name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="campaign_subject">Subject Line *</Label>
              <Input
                id="campaign_subject"
                placeholder="e.g., Get 50% off everything this weekend!"
                value={formData.campaign_subject}
                onChange={(e) => updateField('campaign_subject', e.target.value)}
              />
              {errors.campaign_subject && (
                <p className="text-sm text-destructive">{errors.campaign_subject}</p>
              )}
              <p className="text-xs text-muted-foreground">
                This will appear in your recipients' inboxes
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                placeholder="Internal notes about this campaign..."
                value={formData.description}
                onChange={(e) => updateField('description', e.target.value)}
                rows={3}
              />
            </div>

            <Separator />

            {/* Domain Selector with Email Configuration */}
            <DomainSelector
              organisationId={user?.organisationId || null}
              selectedDomainId={formData.email_domain_id}
              emailPrefix={formData.email_prefix}
              senderName={formData.sender_name}
              onDomainChange={(domainId) => updateField('email_domain_id', domainId)}
              onEmailPrefixChange={(prefix) => updateField('email_prefix', prefix)}
              onSenderNameChange={(name) => updateField('sender_name', name)}
              disabled={saving}
            />
            {(errors.email_domain_id || errors.email_prefix || errors.sender_name) && (
              <div className="space-y-1">
                {errors.email_domain_id && (
                  <p className="text-sm text-destructive">{errors.email_domain_id}</p>
                )}
                {errors.email_prefix && (
                  <p className="text-sm text-destructive">{errors.email_prefix}</p>
                )}
                {errors.sender_name && (
                  <p className="text-sm text-destructive">{errors.sender_name}</p>
                )}
              </div>
            )}

            <Separator />

            {/* Scheduling Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Scheduling</h3>

              <RadioGroup
                value={formData.schedule_type}
                onValueChange={(value) => updateField('schedule_type', value as 'now' | 'scheduled')}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="now" id="now" />
                  <Label htmlFor="now" className="font-normal cursor-pointer">
                    Send immediately after saving
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="scheduled" id="scheduled" />
                  <Label htmlFor="scheduled" className="font-normal cursor-pointer">
                    Schedule for later
                  </Label>
                </div>
              </RadioGroup>

              {formData.schedule_type === 'scheduled' && (
                <div className="flex flex-col md:flex-row md:items-end gap-4">
                  {/* Send Date */}
                  <div className="flex flex-col space-y-2">
                    <Label>Send Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full md:w-[200px] pl-3 text-left font-normal h-10",
                            !formData.schedule_date && "text-muted-foreground"
                          )}
                        >
                          {formData.schedule_date ? (
                            format(formData.schedule_date, "PPP")
                          ) : (
                            <span>Pick a date</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.schedule_date || undefined}
                          onSelect={(date) => updateField('schedule_date', date || null)}
                          disabled={(date) =>
                            date < new Date(new Date().setHours(0, 0, 0, 0))
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Send Time */}
                  <div className="flex flex-col space-y-2">
                    <Label>Send Time</Label>
                    <Select
                      value={formData.schedule_time}
                      onValueChange={(value) => updateField('schedule_time', value)}
                    >
                      <SelectTrigger className="w-full md:w-[140px] h-10">
                        <SelectValue placeholder="Select time" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {times.map((time) => (
                          <SelectItem key={time} value={time}>
                            {time}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
          <CardFooter className="flex justify-end">
            <Button onClick={nextStep} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  Next: Select Audience
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </CardFooter>
        </Card>
      )}

      {/* Step 2: Audience Selection */}
      {currentStep === 'audience' && (
        <Card>
          <CardHeader>
            <CardTitle>Select Audience</CardTitle>
            <CardDescription>
              Choose who will receive this email campaign
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AudienceSelector
              organisationId={user?.organisationId || ''}
              restaurantId={user?.restaurantId}
              userRestaurants={user?.restaurants || []}
              initialAudienceType={formData.audience_type}
              initialFilters={formData.audience_filters}
              initialSelectedRestaurants={formData.selected_restaurants}
              onChange={handleAudienceChange}
            />
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button variant="outline" onClick={previousStep}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <Button onClick={nextStep} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  Next: Create Content
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </CardFooter>
        </Card>
      )}

      {/* Step 3: Email Content */}
      {currentStep === 'content' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Email Content</CardTitle>
                <CardDescription>
                  Write your email content and personalize with variables
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => setShowTemplateLibrary(true)}
                  variant="outline"
                  size="sm"
                >
                  <FolderOpen className="mr-2 h-4 w-4" />
                  Load Template
                </Button>
                <Button
                  onClick={handleSaveAsTemplate}
                  variant="outline"
                  size="sm"
                >
                  <Save className="mr-2 h-4 w-4" />
                  Save as Template
                </Button>
                <Button
                  onClick={handleManualSave}
                  disabled={manualSaving}
                  variant="outline"
                  size="sm"
                >
                  {manualSaving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Save
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0 pb-6">
            <GrapesJSEditor
              initialHtml={formData.campaign_content_html}
              initialProject={formData.campaign_design_json}
              projectId={activeCampaignId}
              projectType="campaign"
              height="600px"
              onChange={(html, css, project) => {
                console.log('[Campaign Form] GrapesJS onChange triggered, HTML length:', html.length);
                setFormData(prev => ({
                  ...prev,
                  campaign_content_html: html,
                  campaign_content_text: html.replace(/<[^>]*>/g, ''), // Strip HTML for plain text
                  campaign_design_json: project, // Store GrapesJS project data
                }));
              }}
              onEditorReady={(saveFunction, editorInstance) => {
                console.log('[Campaign Form] GrapesJS editor ready, save function and editor instance captured');
                editorSaveRef.current = saveFunction;
                editorInstanceRef.current = editorInstance;
              }}
            />
            {errors.campaign_content_html && (
              <p className="text-sm text-destructive mt-2">{errors.campaign_content_html}</p>
            )}
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button variant="outline" onClick={previousStep}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <Button onClick={nextStep}>
              Next: Preview
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardFooter>
        </Card>
      )}

      {/* Step 4: Preview & Send */}
      {currentStep === 'preview' && (() => {
        // Calculate validation on render (Phase 4.1)
        const validation = validateRequiredVariables(formData.campaign_content_html);
        const validationMessages = getValidationMessages(validation);
        const extractedVariables = extractVariablesFromContent(formData.campaign_content_html);
        const groupedVariables = groupVariablesByCategory(extractedVariables);
        const hasVariables = extractedVariables.length > 0;
        const hasErrors = validationMessages.errors.length > 0;

        return (
          <Card>
            <CardHeader>
              <CardTitle>Preview & Send</CardTitle>
              <CardDescription>
                Review your campaign before sending
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              {/* Validation Errors/Warnings (Phase 4.1) */}
              {(validationMessages.errors.length > 0 || validationMessages.warnings.length > 0) && (
                <div className="space-y-3 mb-6">
                  {validationMessages.errors.map((error, index) => (
                    <Alert key={`error-${index}`} variant="destructive">
                      <AlertCircle className="h-4 w-4" style={{ stroke: 'white', color: 'white' }} />
                      <AlertTitle className="!text-white">Required Variable Missing</AlertTitle>
                      <AlertDescription className="!text-white">{error}</AlertDescription>
                    </Alert>
                  ))}
                  {validationMessages.warnings.map((warning, index) => (
                    <Alert key={`warning-${index}`}>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertTitle>Warning</AlertTitle>
                      <AlertDescription>{warning}</AlertDescription>
                    </Alert>
                  ))}
                </div>
              )}

              {/* Two-Column Layout */}
              <div className="grid grid-cols-[30%_1fr] gap-6">
                {/* Left Column - Campaign Summary & Variables */}
                <div className="space-y-6">
                  {/* Campaign Summary */}
                  <div className="space-y-4">
                    <div>
                      <Label className="text-muted-foreground text-xs">Campaign Name</Label>
                      <p className="font-medium text-sm">{formData.campaign_name}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">Subject Line</Label>
                      <p className="font-medium text-sm">{formData.campaign_subject}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">Recipients</Label>
                      <p className="font-medium text-sm">{estimatedRecipients.toLocaleString()}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">Estimated Cost</Label>
                      <p className="font-medium text-sm">${estimatedCost.toFixed(2)}</p>
                    </div>
                  </div>

                  {/* Variables in This Email (Phase 4.2 - Collapsible) */}
                  {hasVariables && (
                    <Accordion type="single" collapsible className="w-full">
                      <AccordionItem value="variables" className="border-0">
                        <AccordionTrigger className="hover:no-underline py-3">
                          <div className="flex items-center gap-2">
                            <Info className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm font-semibold">Variables</span>
                            <Badge variant="secondary" className="ml-1 text-xs">
                              {extractedVariables.length}
                            </Badge>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="space-y-3 pt-2">
                            <p className="text-xs text-muted-foreground">
                              These will be replaced with customer data.
                            </p>
                            <div className="space-y-2">
                              {(Object.keys(groupedVariables) as EmailVariableCategory[]).map(category => {
                                const variables = groupedVariables[category];
                                if (variables.length === 0) return null;

                                return (
                                  <div key={category} className="space-y-1.5">
                                    <Label className="text-xs font-medium text-muted-foreground">
                                      {CATEGORY_LABELS[category]}
                                    </Label>
                                    <div className="flex flex-wrap gap-1.5">
                                      {variables.map(variable => {
                                        const metadata = getVariableMetadata(variable);
                                        return (
                                          <Badge
                                            key={variable}
                                            variant={metadata?.isDynamic ? 'secondary' : 'outline'}
                                            className="font-mono text-[10px] py-0"
                                          >
                                            {variable}
                                            {metadata?.isRequired && (
                                              <span className="ml-0.5 text-red-500">*</span>
                                            )}
                                          </Badge>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  )}
                </div>

                {/* Right Column - Email Preview & Customer Preview */}
                <div className="space-y-4">
                  {/* Customer Preview with Variable Resolution (Phase 4.2c - Dialog Based) */}
                  {hasVariables && (
                    <div className="flex gap-3 items-end">
                      <div className="flex-1">
                        <Label htmlFor="customer-selector" className="text-xs">Preview with Customer Data</Label>
                        <Select
                          value={selectedCustomerId}
                          onValueChange={setSelectedCustomerId}
                        >
                          <SelectTrigger id="customer-selector" className="mt-1 h-9">
                            <SelectValue placeholder="Choose a customer..." />
                          </SelectTrigger>
                          <SelectContent>
                            {recentCustomers.map(customer => (
                              <SelectItem key={customer.id} value={customer.id}>
                                {customer.first_name} {customer.last_name} ({customer.email})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        onClick={handleGeneratePreview}
                        disabled={!selectedCustomerId || previewLoading}
                        variant="default"
                        size="sm"
                        className="h-9"
                      >
                        {previewLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Eye className="mr-2 h-4 w-4" />
                            Generate Preview
                          </>
                        )}
                      </Button>
                    </div>
                  )}

                  {/* Email Preview */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-muted-foreground">Email Preview</Label>
                      <div className="flex gap-1">
                        <Button
                          variant={previewDeviceSize === 'mobile' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setPreviewDeviceSize('mobile')}
                          className="h-7 px-2"
                        >
                          <Smartphone className="h-3 w-3" />
                        </Button>
                        <Button
                          variant={previewDeviceSize === 'tablet' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setPreviewDeviceSize('tablet')}
                          className="h-7 px-2"
                        >
                          <Tablet className="h-3 w-3" />
                        </Button>
                        <Button
                          variant={previewDeviceSize === 'desktop' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setPreviewDeviceSize('desktop')}
                          className="h-7 px-2"
                        >
                          <Monitor className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    <div className="border rounded-md overflow-auto bg-gray-100 p-4 flex items-start justify-center max-h-[600px]">
                      <div
                        className="border rounded-md overflow-hidden bg-white shadow-lg transition-all duration-300 h-[600px]"
                        style={{
                          width: previewDeviceSize === 'mobile' ? '375px' :
                                 previewDeviceSize === 'tablet' ? 'min(768px, 100%)' :
                                 '100%',
                          maxWidth: '100%'
                        }}
                      >
                        <iframe
                          srcDoc={formData.campaign_content_html}
                          className="w-full h-full border-0"
                          title="Email Preview"
                          sandbox="allow-same-origin"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button variant="outline" onClick={previousStep}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => handleSave(false)}
                  disabled={saving}
                >
                  {saving ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="mr-2 h-4 w-4" />
                  )}
                  Save as Draft
                </Button>
                <Button
                  onClick={() => handleSave(true)}
                  disabled={saving || hasErrors}
                >
                  {saving ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Mail className="mr-2 h-4 w-4" />
                  )}
                  {formData.schedule_type === 'now' ? 'Send Campaign' : 'Schedule Campaign'}
                </Button>
              </div>
            </CardFooter>

            {/* Preview Dialog (Phase 4.2c) */}
            {resolvedPreview && (
              <PreviewDialog
                open={showPreviewDialog}
                onOpenChange={setShowPreviewDialog}
                resolvedPreview={resolvedPreview}
              />
            )}
          </Card>
        );
      })()}

      {/* Template Library Modal */}
      {user?.organisationId && (
        <TemplateLibraryModal
          open={showTemplateLibrary}
          onOpenChange={setShowTemplateLibrary}
          onTemplateSelect={handleTemplateSelect}
          organisationId={user.organisationId}
          restaurantId={user.restaurantId}
        />
      )}

      {/* Template Save Dialog */}
      {user?.organisationId && showTemplateSaveDialog && (
        <TemplateSaveDialog
          open={showTemplateSaveDialog}
          onClose={() => setShowTemplateSaveDialog(false)}
          designJson={formData.campaign_design_json || {}}
          templateContentHtml={formData.campaign_content_html}
          organisationId={user.organisationId}
          restaurantId={user.restaurantId}
          onSaveSuccess={handleTemplateSaved}
        />
      )}
    </div>
  );
};

export default CampaignForm;
